import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { HiBuildingOffice2, HiUserGroup } from 'react-icons/hi2'
import { apiClient } from '../common/apiClient'
import { useAuth } from '../contexts/AuthContext'
import { AnimatePresence, motion } from 'framer-motion'
import UserPreferencesModal from './UserPreferencesModal'

interface UserPermissions {
  isOverride?: boolean
  overrideEmail?: string
  [key: string]: string | boolean | undefined
}

interface UserProfileDropdownProps {
  userPermissions?: UserPermissions
  isOpen?: boolean
  onToggle?: (isOpen: boolean) => void
  enableAdminOverride?: boolean
  showSettings?: boolean
  onViewAsUser?: () => void
  onClearOverride?: () => void
  placement?: 'top' | 'bottom'
  showTriggerPicture?: boolean
}

// Helper function to get initials from name
const getInitials = (name?: string, email?: string): string => {
  if (name) {
    const parts = name.trim().split(' ')
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
    }
    return name.charAt(0).toUpperCase()
  }
  return email?.charAt(0).toUpperCase() || '?'
}

const formatCompanyName = (company?: string): string => company || 'N/A'

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  userPermissions = {},
  isOpen = false,
  onToggle,
  enableAdminOverride = false,
  showSettings = false,
  onViewAsUser,
  onClearOverride,
  placement = 'top',
  showTriggerPicture = true,
}) => {
  const { user, getGraphAccessToken, loginWithAccountSwitching } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [userProfilePicture, setUserProfilePicture] = useState<string | null>(null)
  const [overrideProfilePicture, setOverrideProfilePicture] = useState<string | null>(null)
  const [showPreferencesModal, setShowPreferencesModal] = useState(false)
  const [highlightSetting, setHighlightSetting] = useState<'team_bar' | undefined>(undefined)
  const [isLoaded, setIsLoaded] = useState(false)

  // Flag the profile icon if the logged-in staff member still needs to finish their Wise
  // setup (proof of account and/or Wise email). Non-PH staff always return false.
  const wiseStatusEmail = userPermissions?.isOverride ? userPermissions?.overrideEmail : undefined
  const { data: wiseStatus } = useQuery<{ outstanding: boolean }>({
    queryKey: ['wise-my-status', wiseStatusEmail ?? 'self'],
    queryFn: () =>
      apiClient.get<{ outstanding: boolean }>(
        `/api/wise-accounts/my-status${wiseStatusEmail ? `?email=${encodeURIComponent(wiseStatusEmail)}` : ''}`
      ),
    staleTime: 5 * 60 * 1000,
  })
  const wiseOutstanding = wiseStatus?.outstanding === true

  // Open modal if URL is /preferences on mount or location change
  useEffect(() => {
    if (location.pathname === '/preferences') {
      setShowPreferencesModal(true)
    }
  }, [location.pathname])

  // Sync URL when modal opens/closes
  const openPreferences = () => {
    setShowPreferencesModal(true)
    window.history.pushState({}, '', '/preferences')
  }

  const closePreferences = () => {
    setShowPreferencesModal(false)
    setHighlightSetting(undefined)
    window.history.pushState({}, '', '/')
  }

  // Load profile picture from user object (fetched from Microsoft Graph)
  useEffect(() => {
    if (user?.profilePicture) {
      setUserProfilePicture(user.profilePicture)
    } else {
      setUserProfilePicture(null)
    }
  }, [user?.profilePicture])

  // Set loaded state when user data is available
  useEffect(() => {
    if (user?.name || user?.email) {
      setIsLoaded(true)
    }
  }, [user?.name, user?.email])

  // Listen for open-preferences event from FloatingTeamBar
  useEffect(() => {
    const handleOpenPreferences = (e: CustomEvent<{ highlight?: 'team_bar' }>) => {
      setHighlightSetting(e.detail.highlight)
      openPreferences()
    }
    window.addEventListener('open-preferences', handleOpenPreferences as EventListener)
    return () => window.removeEventListener('open-preferences', handleOpenPreferences as EventListener)
  }, [])

  // Fetch override profile picture when in admin override mode
  useEffect(() => {
    const fetchOverrideProfilePicture = async (email: string) => {
      try {
        const token = await getGraphAccessToken()
        if (!token) return

        // Using 432x432 size for better performance
        const photoResponse = await fetch(
          `https://graph.microsoft.com/v1.0/users/${email}/photos/432x432/$value`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )

        if (photoResponse.ok) {
          const photoBlob = await photoResponse.blob()
          const photoUrl = URL.createObjectURL(photoBlob)
          setOverrideProfilePicture(photoUrl)
        } else {
          setOverrideProfilePicture(null)
        }
      } catch (error) {
        setOverrideProfilePicture(null)
      }
    }

    if (userPermissions.isOverride && userPermissions.overrideEmail) {
      fetchOverrideProfilePicture(userPermissions.overrideEmail)
    } else {
      setOverrideProfilePicture(null)
    }

    // Cleanup blob URLs
    return () => {
      if (overrideProfilePicture) {
        URL.revokeObjectURL(overrideProfilePicture)
      }
    }
  }, [userPermissions.isOverride, userPermissions.overrideEmail, getGraphAccessToken])

  // In override mode show the impersonated person's picture (or fall back to their
  // initials) — never the admin's own photo. Otherwise use the user's own picture.
  const displayPicture = userPermissions.isOverride ? overrideProfilePicture : userProfilePicture

  // Determine display info - use override info if in override mode, otherwise use actual user
  const displayName =
    userPermissions.isOverride && userPermissions.overrideEmail
      ? (userPermissions.Name as string) || userPermissions.overrideEmail
          .split('@')[0]
          .replace('.', ' ')
          .split(' ')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ')
      : user?.name || 'User'
  // Bold the real first name (Salesforce FirstName can be multi-word, e.g. "Shiv Srivar")
  // rather than naively bolding just the first word.
  const firstName =
    (userPermissions.isOverride && (userPermissions.FirstName as string)) ||
    displayName.split(' ')[0]
  const restName = displayName.startsWith(firstName)
    ? displayName.slice(firstName.length).trimStart()
    : displayName.split(' ').slice(1).join(' ')
  const displayEmail = userPermissions.isOverride
    ? userPermissions.overrideEmail
    : user?.email || user?.username
  const displayCompany = userPermissions.isOverride
    ? (userPermissions.Company__c as string)
    : user?.company
  const displayTeam = userPermissions.isOverride ? (userPermissions.Team__c as string) : user?.team

  return (
    <div
      className="relative"
      onMouseEnter={() => onToggle?.(true)}
      onMouseLeave={() => onToggle?.(false)}
    >
      <div
        className={`flex cursor-pointer items-center gap-3 rounded-md px-3 transition-all duration-200 ${
          placement === 'bottom' ? 'h-9' : 'py-1.5'
        } ${
          placement === 'bottom' ? 'bg-gray-100 hover:bg-gray-200' : 'hover:bg-white/50'
        } ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={() => onToggle?.(!isOpen)}
      >
        {showTriggerPicture && (
          <div className="relative flex-shrink-0">
            <div className="flex items-center justify-center w-5 h-5 rounded-full overflow-hidden">
              {displayPicture ? (
                <img
                  src={displayPicture}
                  alt={displayName}
                  className="w-full h-full object-cover"
                  onError={() => setUserProfilePicture(null)}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                  <span className="text-white font-semibold text-[10px]">
                    {getInitials(displayName, displayEmail)}
                  </span>
                </div>
              )}
            </div>
            {wiseOutstanding && (
              <span
                title="Wise account setup outstanding"
                className="absolute -top-0.5 -right-0.5 block w-2 h-2 rounded-full bg-red-500 ring-1 ring-white"
              />
            )}
          </div>
        )}
        <div className="flex-1 flex flex-col">
          <span className="flex items-center whitespace-nowrap text-base font-medium text-gray-700">
            <span className="font-black">{firstName}</span>
            {restName && <span className="ml-1 font-medium">{restName}</span>}
            {!showTriggerPicture && wiseOutstanding && (
              <span
                title="Wise account setup outstanding"
                className="ml-2 block h-2 w-2 rounded-full bg-red-500"
              />
            )}
          </span>
        </div>
      </div>

      {/* Modern dropdown menu on hover with fade animation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={`absolute z-50 ${
              placement === 'bottom' ? 'right-0 top-full mt-2' : 'bottom-full left-0 mb-2'
            }`}
          >
            <div className="w-80 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 p-4 space-y-3">
                <div className="flex items-center space-x-3">
                  {displayPicture ? (
                    <img
                      src={displayPicture}
                      alt={displayName}
                      className="w-12 h-12 rounded object-cover"
                      onError={() => setUserProfilePicture(null)}
                    />
                  ) : (
                    <div className="w-12 h-12 bg-gray-900 rounded flex items-center justify-center">
                      <span className="text-white font-medium text-sm">
                        {getInitials(displayName, displayEmail)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                    <p className="text-xs text-gray-500 truncate">{displayEmail}</p>
                  </div>
                </div>
                <div className="space-y-2 pt-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <HiBuildingOffice2 className="w-3.5 h-3.5 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">Company:</span>
                    </div>
                    <span className="text-xs text-gray-900 font-medium">
                      {formatCompanyName(displayCompany)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <HiUserGroup className="w-3.5 h-3.5 text-gray-400 mr-2" />
                      <span className="text-xs text-gray-500">Team:</span>
                    </div>
                    <span className="text-xs text-gray-900 font-medium">
                      {displayTeam || 'N/A'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => navigate('/my-profile')}
                  className="w-full py-2 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded hover:bg-blue-50 transition-colors"
                >
                  View My Profile
                </button>
              </div>
              <div className="border-t border-gray-200 px-4 py-3 bg-white flex flex-col gap-2">
                {enableAdminOverride && showSettings && !userPermissions.isOverride && (
                  <button
                    onClick={onViewAsUser}
                    className="w-full py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    View As User
                  </button>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={openPreferences}
                    className="flex-1 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    Preferences
                  </button>
                  <button
                    onClick={loginWithAccountSwitching}
                    className="flex-1 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                  >
                    Switch account
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* User Preferences Modal */}
      <UserPreferencesModal
        isOpen={showPreferencesModal}
        onClose={closePreferences}
        userEmail={displayEmail}
        userTeam={displayTeam}
        highlightSetting={highlightSetting}
        getGraphAccessToken={getGraphAccessToken}
      />
    </div>
  )
}

export default UserProfileDropdown
